<div class="reportList">
  <p-table #dt
    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} reportes"
    styleClass="reportList__table"
    [filterDelay]="0"
    [globalFilterFields]="searchFields"
    [loading]="reportService.loading"
    [paginator]="true"
    [rows]="10"
    [rowHover]="true"
    [rowsPerPageOptions]="[10, 25, 50]"
    [showCurrentPageReport]="true"
    [value]="reports">

    <!-- ------------------------------ CAPTION ------------------------------ -->
    <ng-template pTemplate="caption">
      <div class="reportList__caption">

        <label class="reportList__search">
          <i class="material-icons reportList__searchIcon" aria-hidden="true">search</i>
          <span class="visually-hidden">Buscar reportes</span>
          <input
            type="text"
            class="reportList__searchInput"
            placeholder="Buscar por título, ubicación o quien reporta"
            (input)="search(dt, $event)">
        </label>

        <span class="reportList__captionCount">
          {{ resultsLabel(dt.filteredValue?.length) }}
        </span>

      </div>
    </ng-template>

    <!-- ------------------------------ HEADER ------------------------------- -->
    <ng-template pTemplate="header">
      <tr>
        <th pSortableColumn="title" class="reportList__columnReport">
          Reporte
          <p-sortIcon field="title"></p-sortIcon>
        </th>

        <th pSortableColumn="streetBuilding.name" class="reportList__columnLocation">
          Ubicación
          <p-sortIcon field="streetBuilding.name"></p-sortIcon>
        </th>

        <th pSortableColumn="createdBy.names" class="reportList__columnPerson d-none d-xl-table-cell">
          Reportado por
          <p-sortIcon field="createdBy.names"></p-sortIcon>
        </th>

        <th pSortableColumn="createdAt" class="reportList__columnDate d-none d-lg-table-cell">
          Fecha
          <p-sortIcon field="createdAt"></p-sortIcon>
        </th>

        <th pSortableColumn="status" class="reportList__columnStatus">
          Estado
          <p-sortIcon field="status"></p-sortIcon>
        </th>

        <th class="reportList__columnActions">
          <span class="visually-hidden">Acciones</span>
        </th>
      </tr>
    </ng-template>

    <!-- ------------------------------- BODY -------------------------------- -->
    <ng-template pTemplate="body" let-report>
      <!-- Toda la fila abre el detalle. Es un atajo de ratón: sin tabindex ni
           role, el camino de teclado sigue siendo el botón del título. -->
      <tr class="reportList__row" (click)="openFromRow(report)">

        <!-- Título + resumen. La descripción completa vive en el detalle: aquí
             solo se muestra la primera línea para que la fila no crezca. -->
        <td class="reportList__columnReport">
          <button
            type="button"
            class="reportList__titleButton"
            (click)="openFromControl(report, $event)">
            <span class="reportList__title">{{ report.title }}</span>
          </button>

          <p class="reportList__description mb-0">{{ report.description }}</p>

          @if (report.files.length) {
            <span class="reportList__attachment">
              <i class="material-icons reportList__attachmentIcon" aria-hidden="true">photo_library</i>
              {{ attachmentLabel(report) }}
            </span>
          }

          <!-- Motivo de la cancelación. Solo existe en la bandeja de
               cancelados: es el dato que explica por qué ese reporte ya no se
               va a atender, así que se ve sin abrir el detalle. Se trunca a una
               línea para no romper la altura de la fila; el texto completo va
               en el title y en el modal. -->
          @if (cancelReasonLabel(report); as reasonLabel) {
            <p
              class="reportList__cancelReason mb-0"
              [title]="'Motivo de la cancelación: ' + reasonLabel">
              <i class="material-icons reportList__cancelReasonIcon" aria-hidden="true">cancel</i>
              <span class="text-truncate">{{ reasonLabel }}</span>
            </p>
          }
        </td>

        <!-- Ubicación -->
        <td class="reportList__columnLocation">
          <span class="reportList__location">

            <!-- La foto de la amenidad sustituye al icono cuando existe. Va
                 muda para el lector de pantalla: el nombre ya está al lado. Si
                 no carga, (error) marca el reporte y se cae al icono. -->
            @if (amenityImage(report); as imageUrl) {
              <img
                class="reportList__locationImage"
                [src]="imageUrl"
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                (error)="onAmenityImageError(report)">
            } @else {
              <i class="material-icons reportList__locationIcon" aria-hidden="true">{{ locationIcon(report) }}</i>
            }

            <span class="text-truncate" [title]="locationLabel(report)">{{ locationLabel(report) }}</span>
          </span>
        </td>

        <!-- Quién lo reportó -->
        <td class="reportList__columnPerson d-none d-xl-table-cell">
          <span class="reportList__person text-truncate" [title]="reportedByLabel(report)">
            {{ reportedByLabel(report) }}
          </span>

          <!-- Domicilio del residente que reportó, no la ubicación del
               problema (esa es la columna de al lado). Solo existe cuando el
               reporte vino de la app; si lo creó un admin la celda se queda
               con el nombre. La columna es angosta: se trunca y el texto
               completo va en el title. -->
          @if (reportedByAddressLabel(report); as addressLabel) {
            <span
              class="reportList__personAddress text-truncate"
              [title]="'Domicilio de quien reportó: ' + addressLabel">
              {{ addressLabel }}
            </span>
          }
        </td>

        <!-- Fecha exacta arriba, antigüedad abajo -->
        <td class="reportList__columnDate d-none d-lg-table-cell">
          <time class="reportList__date" [attr.datetime]="dateTime(report)">
            {{ dateLabel(report) }}
          </time>
          <span class="reportList__dateRelative">{{ relativeDateLabel(report) }}</span>
        </td>

        <!-- Estado: color + icono + texto, nunca color solo -->
        <td class="reportList__columnStatus">
          <span class="reportList__status reportList__status{{ statusVariant(report) }}">
            <i class="material-icons reportList__statusIcon" aria-hidden="true">{{ statusIcon(report) }}</i>
            {{ report.statusLabel }}
          </span>
        </td>

        <!-- Acciones. El click se queda en la celda: abrir el menú no puede
             abrir además el detalle. El menú vive en <body> (container="body"),
             así que sus opciones nunca burbujean hasta la fila. -->
        <td class="reportList__columnActions" (click)="$event.stopPropagation()">

          <div ngbDropdown placement="bottom-end" container="body">
            <button
              type="button"
              class="btn reportList__actionsButton"
              [id]="report.reportId"
              ngbDropdownToggle
              [attr.aria-label]="'Acciones del reporte ' + report.title">
              <i class="material-icons reportList__actionsIcon" aria-hidden="true">more_vert</i>
            </button>

            <div ngbDropdownMenu>

              <button
                ngbDropdownItem
                class="d-flex align-items-center"
                (click)="open(report)">
                <i class="material-icons reportList__menuIcon" aria-hidden="true">article</i>
                Ver detalles
              </button>

              <!-- Las tres acciones destructivas o de avance se preguntan al
                   MODELO, nunca al status pelado: aquí el contexto es
                   `let-report` (o sea `any`) y una condición mal escrita no la
                   detecta el compilador. Los mismos getters gobiernan el pie
                   del modal de detalle. -->
              @if (report.canAdvanceStatus) {
                <button
                  ngbDropdownItem
                  class="d-flex align-items-center"
                  (click)="updateStatus(report)">
                  <i class="material-icons reportList__menuIcon" aria-hidden="true">{{ nextStatusIcon(report) }}</i>
                  Pasar a {{ report.nextStatusString.toLowerCase() }}
                </button>
              }

              @if (report.canBeCanceled || report.canBeDeleted) {
                <div class="dropdown-divider"></div>
              }

              @if (report.canBeCanceled) {
                <button
                  ngbDropdownItem
                  class="d-flex align-items-center text-danger"
                  (click)="cancel(report)">
                  <i class="material-icons reportList__menuIcon" aria-hidden="true">cancel</i>
                  Cancelar
                </button>
              }

              <!-- Borrar y Cancelar se excluyen entre sí: un reporte propio y
                   pendiente se borra, todo lo demás se cancela. -->
              @if (report.canBeDeleted) {
                <button
                  ngbDropdownItem
                  class="d-flex align-items-center text-danger"
                  (click)="delete(report)">
                  <i class="material-icons reportList__menuIcon" aria-hidden="true">delete_outline</i>
                  Borrar
                </button>
              }

            </div>
          </div>

        </td>
      </tr>
    </ng-template>

    <!-- ---------------------------- EMPTY STATE ---------------------------- -->
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="6">
          <div class="reportList__empty">
            <i class="material-icons reportList__emptyIcon" aria-hidden="true">inbox</i>
            <p class="reportList__emptyText mb-0">{{ emptyMessage }}</p>
          </div>
        </td>
      </tr>
    </ng-template>

  </p-table>
</div>
